import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  makeStyles,
  Typography,
  Box,
  Chip,
  IconButton
} from '@material-ui/core';
import VerifiedIcon from 'src/components/icons/VerifiedIcon';
import UnverifiedIcon from 'src/components/icons/UnverifiedIcon';
import ConfirmationDialog from 'src/components/ConfirmationDialog';

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: 20
  },
  kycSection: {
    marginTop: theme.spacing(2)
  },
  fieldMatch: {
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  mono: {
    fontFamily: 'monospace'
  }
}));

// QoreID outcome -> chip colour + human label. Mirrors the backend verdicts in
// VerificationService (verified | needs_review | rejected | error | duplicate).
const OUTCOME_META = {
  verified: { label: 'Verified', color: '#43a047' },
  needs_review: { label: 'Needs review', color: '#fb8c00' },
  rejected: { label: 'Rejected', color: '#e53935' },
  duplicate: { label: 'Possible duplicate', color: '#e53935' },
  error: { label: 'Provider error', color: '#757575' }
};

const DocumentDetails = ({
  className,
  customer,
  handleVerification,
  ...rest
}) => {
  const classes = useStyles();

  const kyc = customer.kycVerification;
  const outcomeMeta =
    kyc && OUTCOME_META[kyc.outcome]
      ? OUTCOME_META[kyc.outcome]
      : { label: kyc ? kyc.outcome : 'Not run', color: '#757575' };
  const fieldMatches = (kyc && kyc.fieldMatches) || {};

  const toggleDocVerification = () => {
    handleVerification({ idVerify: !customer.isIdVerified });
  };
  const toggleLicenceVerification = () => {
    handleVerification({ licenceVerify: !customer.isDriverLicenceVerified });
  };
  return (
    <div className={clsx(classes.root, className)} {...rest}>
      <Card>
        <CardHeader
          title={
            <Box display="flex" alignItems="center">
              <Typography variant="h6">Documents</Typography>
              {/* Manual override: rider identity / idVerify (deliberate fallback). */}
              <ConfirmationDialog
                title={`${
                  customer.isIdVerified ? 'Unverify' : 'Verify'
                } customer rider identity`}
                body="Are you sure ?"
              >
                {confirm => (
                  <Box component="span" ml={1}>
                    <IconButton
                      aria-label={`${
                        customer.isIdVerified ? 'Unverify' : 'Verify'
                      } rider identity document`}
                      onClick={confirm(toggleDocVerification)}
                    >
                      {customer.isIdVerified ? (
                        <VerifiedIcon fontSize="small" />
                      ) : (
                        <UnverifiedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                )}
              </ConfirmationDialog>
              {/* Manual override: driver's licence / licenceVerify (fallback). */}
              <ConfirmationDialog
                title={`${
                  customer.isDriverLicenceVerified ? 'Unverify' : 'Verify'
                } customer driver's licence`}
                body="Are you sure ?"
              >
                {confirm => (
                  <Box component="span" ml={1}>
                    <IconButton
                      aria-label={`${
                        customer.isDriverLicenceVerified ? 'Unverify' : 'Verify'
                      } driver's licence`}
                      onClick={confirm(toggleLicenceVerification)}
                    >
                      {customer.isDriverLicenceVerified ? (
                        <VerifiedIcon fontSize="small" />
                      ) : (
                        <UnverifiedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                )}
              </ConfirmationDialog>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          {/* QoreID automated KYC (Feature #5) is the canonical identity check.
              Legacy uploaded-ID-image links were retired with the QoreID flow,
              which has no upload step. The manual override toggles above remain
              as a deliberate fallback. */}
          <Box>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6">QoreID identity check</Typography>
              <Box component="span" ml={1}>
                <Chip
                  size="small"
                  label={outcomeMeta.label}
                  style={{ backgroundColor: outcomeMeta.color, color: '#fff' }}
                />
              </Box>
            </Box>

            <Grid container spacing={3}>
              <Grid item md={6} xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Identity (rider) — NIN or passport
                </Typography>
                <Typography className={classes.mono}>
                  {(customer.nin && `NIN ${customer.nin}`) ||
                    (customer.passportNumber &&
                      `Passport ${customer.passportNumber}`) ||
                    '—'}
                </Typography>
                <Typography variant="body2">
                  {customer.isIdVerified
                    ? `Identity verified${
                        { passport: ' (passport)', nin: ' (NIN)' }[
                          kyc && kyc.check
                        ] || ''
                      }`
                    : 'Identity not verified'}
                </Typography>
              </Grid>
              <Grid item md={6} xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Driver&apos;s licence (driver)
                </Typography>
                <Typography className={classes.mono}>
                  {customer.driversLicenceNumber || '—'}
                </Typography>
                <Typography variant="body2">
                  {customer.isDriverLicenceVerified
                    ? 'Licence verified'
                    : 'Licence not verified'}
                </Typography>
              </Grid>
            </Grid>

            {kyc ? (
              <Box mt={2}>
                <Typography variant="subtitle2" color="textSecondary">
                  Field matches
                </Typography>
                <Box display="flex" flexWrap="wrap">
                  {Object.keys(fieldMatches).length === 0 ? (
                    <Typography variant="body2">
                      No field-level data returned.
                    </Typography>
                  ) : (
                    Object.keys(fieldMatches).map(field => (
                      <Chip
                        key={field}
                        size="small"
                        className={classes.fieldMatch}
                        label={field}
                        style={{
                          backgroundColor: fieldMatches[field]
                            ? '#43a047'
                            : '#e53935',
                          color: '#fff'
                        }}
                      />
                    ))
                  )}
                </Box>
                {kyc.checkedAt && (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    component="p"
                    style={{ marginTop: 8 }}
                  >
                    Last checked{' '}
                    {new Date(kyc.checkedAt).toLocaleString()} via{' '}
                    {kyc.provider || 'qoreid'}
                    {kyc.providerId ? ` (ref ${kyc.providerId})` : ''}
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No automated check has been run — use the manual document review
                above.
              </Typography>
            )}
          </Box>
        </CardContent>
        <Divider />
      </Card>
    </div>
  );
};

DocumentDetails.propTypes = {
  className: PropTypes.string,
  customer: PropTypes.object,
  handleVerification: PropTypes.func
};

export default DocumentDetails;
